import React from "react";
import { Loader2, CheckCircle2, Download, AlertTriangle } from "lucide-react";
import { cn } from "~/lib/utils";
import { ViewState, UsageBreakdown } from "./types";

// // // //

export function DataExportModalHeader(props: {
    viewState: ViewState;
    usageBreakdown: UsageBreakdown | null;
}) {
    const { viewState, usageBreakdown } = props;

    const iconWrapperClassName =
        "mb-5 flex h-14 w-14 items-center justify-center rounded-full";
    const iconClassName = "h-7 w-7";
    const titleClassName = "text-xl font-semibold text-foreground";
    const descriptionClassName = "mt-2 text-sm text-muted-foreground";

    switch (viewState) {
        case "loading_usage":
            return (
                <>
                    <div
                        className={cn(iconWrapperClassName, "bg-primary-light")}
                    >
                        <Loader2
                            className={cn(
                                iconClassName,
                                "animate-spin text-primary",
                            )}
                        />
                    </div>
                    <h2 className={titleClassName}>
                        Checking your download usage...
                    </h2>
                    <p className={descriptionClassName}>
                        This should only take a moment...
                    </p>
                </>
            );
        case "limit_exceeded":
            return (
                <>
                    <div className={cn(iconWrapperClassName, "bg-amber-50")}>
                        <AlertTriangle
                            className={cn(iconClassName, "text-amber-600")}
                        />
                    </div>
                    <h2 className={titleClassName}>Download limit reached</h2>
                    <p className={descriptionClassName}>
                        You&apos;ve hit the download limit for your current
                        plan. Upgrade to keep downloading data.
                    </p>
                </>
            );
        case "awaiting_confirmation":
            if (!usageBreakdown) return null;
            return (
                <>
                    <div
                        className={cn(iconWrapperClassName, "bg-primary-light")}
                    >
                        <Download
                            className={cn(iconClassName, "text-primary")}
                        />
                    </div>
                    <h2 className={titleClassName}>Confirm your download</h2>
                    <p className={descriptionClassName}>
                        You&apos;ve used {usageBreakdown.count} /{" "}
                        {usageBreakdown.limit} of your {usageBreakdown.period}{" "}
                        limit.
                    </p>
                </>
            );
        case "preparing_download":
            return (
                <>
                    <div
                        className={cn(iconWrapperClassName, "bg-primary-light")}
                    >
                        <Loader2
                            className={cn(
                                iconClassName,
                                "animate-spin text-primary",
                            )}
                        />
                    </div>
                    <h2 className={titleClassName}>Preparing your download</h2>
                    <p className={descriptionClassName}>
                        This should only take a moment...
                    </p>
                </>
            );
        case "download_ready":
            return (
                <>
                    <div className={cn(iconWrapperClassName, "bg-emerald-50")}>
                        <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">
                        Your download is ready
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Your file should download automatically.
                    </p>
                </>
            );
    }
}
